import { CLASS_NAMES, MISC } from '@bs/constants';

/**
 * StatsPercentageCounter
 */
export default class StatsPercentageCounter {
  /**
   * Constructor for StatCounter which simply assigns the ScrollService
   * to a property on the constructor for reference.
   * @param {HTMLElement} element - REQUIRED - the module's container
   * @param {object} Services various services, passed in as param
   */
  constructor(element, Services) {
    this.element = element;
    this.ScrollService = Services.ScrollService;
    this.animating = false;
    this.animated = false;
    this.prepend = '';

    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  cacheDomReferences() {
    this.counter = this.element.querySelector(
      `.${CLASS_NAMES.TWO_UP_STATS_COUNTER}`
    );
    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  setupHandlers() {
    this.onScrollHandler = this.onScroll.bind(this);
    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  enable() {
    window.setTimeout(this.onScrollHandler, 300);
    this.ScrollService.addCallback(this.onScrollHandler);
    return this;
  }

  /**
   * A function which triggers the stat counter once the statistic is in the viewport
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  onScroll() {
    if (
      this.counter.dataset.visible === MISC.TRUE &&
      !this.animating &&
      !this.animated
    ) {
      this.element.dispatchEvent(
        new CustomEvent('percentageCounterStart', {
          bubbles: true,
          detail: {
            targetPct: this.counter.dataset.targetPct
          }
        })
      );
      this.countUp();
    }
    return this;
  }

  /**
   * A function which finds the first numbers in a string, converts them to floating point numbers,
   * and animates them counting up from 0
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  countUp() {
    const targetValue = parseFloat(this.counter.dataset.targetPct);
    if (!Number.isNaN(targetValue)) {
      const duration = targetValue <= 50 ? 1500 : 2000; // Set duration based on target value
      this.animateNumber(targetValue, duration);
    }
    return this;
  }

  /**
   * A function which animates the counting of a number using a sliding effect.
   * @param {number} target - the number to animate to
   * @param {number} duration - how long to take to animate, in milliseconds
   * @returns {object} A reference to the current instance of this class
   * @chainable
   */
  animateNumber(target, duration) {
    if (target) {
      this.animating = true;

      const counterDiv = this.counter.querySelector(
        `.${CLASS_NAMES.TWO_UP_STATS_COUNTER_VALUE}`
      );
      if (!counterDiv) return this;

      let numbersHTML = '';
      for (let i = 0; i <= target; i += 1) {
        numbersHTML += `<div>${i}%</div>`;
      }
      counterDiv.innerHTML = numbersHTML;

      const firstChildHeight = counterDiv.firstElementChild.offsetHeight;
      const totalHeight = counterDiv.offsetHeight - firstChildHeight;

      let startTime = null;

      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const translateY = progress * totalHeight;

        counterDiv.style.transform = `translateY(-${translateY}px)`;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          this.animating = false;
          this.animated = true;
        }
      };

      requestAnimationFrame(animate.bind(this));
    }

    return this;
  }
}
